import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { LOCAL_STORAGE, WINDOW } from '@data-terminal/data-access/injection-tokens';
import { CustomSnackbarComponent, CustomSnackbarData } from '@data-terminal/ui-presentational/components';
import {
    DAYS_IN_WEEK,
    HOURS_IN_DAY,
    MINUTES_IN_HOUR,
    MS_IN_SECOND,
    SECONDS_IN_MIN,
} from '@data-terminal/utils/constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, from, fromEvent, timer } from 'rxjs';
import { exhaustMap, switchMap } from 'rxjs/operators';

export type NetworkStatus = 'online' | 'offline';

export interface NetworkLogEntry {
    timestamp: string;
    status: NetworkStatus;
}

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class NetworkStatusService {
    readonly #snackBar = inject(MatSnackBar);
    readonly #window = inject(WINDOW);
    readonly #localStorage = inject(LOCAL_STORAGE);

    readonly isOnline$ = new BehaviorSubject<boolean>(this.#window.navigator.onLine);

    private offlineSnackRef: MatSnackBarRef<CustomSnackbarComponent> | null = null;
    private wasOffline = !this.#window.navigator.onLine;

    private readonly LOCAL_STORAGE_KEY = 'networkStatusLogs';
    private readonly ONLINE_CHECK_INTERVAL_MS = 10_000;
    private readonly OFFLINE_CHECK_INTERVAL_MS = 3_000;

    // 4 weeks
    private readonly MAX_LOG_AGE_MS = MS_IN_SECOND * SECONDS_IN_MIN * MINUTES_IN_HOUR * HOURS_IN_DAY * DAYS_IN_WEEK * 4;

    constructor() {
        this.filterStoredLogs();

        if (this.wasOffline) {
            this.showOfflineSnackbar();
        }
    }

    public initNetworkListener(): void {
        fromEvent(this.#window, 'online')
            .pipe(untilDestroyed(this))
            .subscribe(() => this.handleOnline());

        fromEvent(this.#window, 'offline')
            .pipe(untilDestroyed(this))
            .subscribe(() => this.handleOffline());

        this.isOnline$
            .pipe(
                untilDestroyed(this),
                switchMap((isOnline) =>
                    timer(
                        isOnline ? this.ONLINE_CHECK_INTERVAL_MS : this.OFFLINE_CHECK_INTERVAL_MS,
                        isOnline ? this.ONLINE_CHECK_INTERVAL_MS : this.OFFLINE_CHECK_INTERVAL_MS
                    )
                ),
                exhaustMap(() => from(this.isConnected()))
            )
            .subscribe((isOnline) => {
                if (isOnline) {
                    this.handleOnline();
                    return;
                }

                this.handleOffline();
            });
    }

    public getStoredLogs(): NetworkLogEntry[] {
        const raw = this.#localStorage.getItem(this.LOCAL_STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    }

    public clear(): void {
        this.#localStorage.removeItem(this.LOCAL_STORAGE_KEY);
    }

    private handleOffline(): void {
        this.isOnline$.next(false);

        if (this.wasOffline) {
            return;
        }

        this.wasOffline = true;
        this.logStatus('offline');
        this.showOfflineSnackbar();
    }

    private handleOnline(): void {
        this.isOnline$.next(true);

        if (this.wasOffline) {
            this.logStatus('online');
            this.hideOfflineSnackbar();
            this.showOnlineSnackbar();
        }

        this.wasOffline = false;
    }

    private async isConnected(): Promise<boolean> {
        if (!this.#window.navigator.onLine) {
            return false;
        }

        try {
            await this.#window.fetch(this.#window.location.href, {
                cache: 'no-store',
                method: 'HEAD',
            });
            return true;
        } catch {
            return false;
        }
    }

    private showOfflineSnackbar(): void {
        if (this.offlineSnackRef) {
            return;
        }

        this.offlineSnackRef = this.#snackBar.openFromComponent(CustomSnackbarComponent, {
            duration: undefined,
            verticalPosition: 'top',
            panelClass: ['hdmui-error', 'snackbar-error-blink'],
            data: {
                translationKey: 'GENERAL.OFFLINE',
            } as CustomSnackbarData,
        });
    }

    private hideOfflineSnackbar(): void {
        if (this.offlineSnackRef) {
            this.offlineSnackRef.dismiss();
            this.offlineSnackRef = null;
        }
    }

    private showOnlineSnackbar(): void {
        this.#snackBar.openFromComponent(CustomSnackbarComponent, {
            duration: 10_000,
            verticalPosition: 'top',
            panelClass: ['hdmui-success'],
            data: {
                icon: 'hdmui:connectionOn',
                translationKey: 'GENERAL.ONLINE',
            } as CustomSnackbarData,
        });
    }

    private logStatus(status: NetworkStatus): void {
        const logs = this.filterLogs(this.getStoredLogs());
        logs.unshift({ timestamp: new Date().toISOString(), status });
        this.saveLogs(logs);
    }

    private filterLogs(logs: NetworkLogEntry[]): NetworkLogEntry[] {
        const now = Date.now();

        return logs.filter((entry) => {
            const timestamp = Date.parse(entry.timestamp);
            if (Number.isNaN(timestamp)) {
                return false;
            }
            return now - timestamp <= this.MAX_LOG_AGE_MS;
        });
    }

    private filterStoredLogs(): void {
        const logs = this.getStoredLogs();
        const filteredLogs = this.filterLogs(logs);

        if (filteredLogs.length !== logs.length) {
            this.saveLogs(filteredLogs);
        }
    }

    private saveLogs(logs: NetworkLogEntry[]): void {
        this.#localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(logs));
    }
}
